import { useState, type ReactNode, type ComponentType } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  SiApple,
  SiSteam,
  SiWalmart,
  SiVisa,
} from 'react-icons/si'
// react-icons no longer ships SiAmazon under its `si` (Simple Icons) export.
// Use a small inline SVG so we don't pull in a second icon library.
function SiAmazon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M14.5 16.6c-.4.4-1 .6-1.5.6-.7 0-1.1-.4-1.1-1 0-.7.5-1.1 1.4-1.1.5 0 1 .1 1.4.2v.5c0 .3 0 .6-.2.8zm2.6 1.6c-.1-.2-.2-.3-.2-.6v-3c0-1.2-.1-2.3-1-3.1-.6-.5-1.6-.8-2.8-.8-1.7 0-3.7.5-4.1 2.7 0 .2.1.3.3.4l1.7.2c.2 0 .3-.1.3-.3.2-.7.7-1.1 1.4-1.1.4 0 .9.1 1.1.5.3.4.2 1 .2 1.5v.3c-1.1 0-2.6.1-3.6.6-1.2.5-2 1.5-2 2.8 0 1.7 1.4 2.6 3 2.6 1.4 0 2.2-.4 3.3-1.5.4.5.5.7 1.2 1.3.1.1.3.1.4 0v0c.4-.4 1.2-1 1.6-1.4.2-.2.1-.4 0-.6zM21.4 19.4c-2.6 1.9-6.5 3-9.7 3-4.6 0-8.7-1.7-11.8-4.5-.2-.2 0-.5.3-.4 3.4 2 7.5 3.1 11.8 3.1 2.9 0 6.1-.6 9-1.9.5-.2.9.3.4.7zM22.6 18c-.3-.4-2.2-.2-3.1-.1-.3 0-.3-.2-.1-.4 1.5-1 3.9-.7 4.2-.4.3.4-.1 2.8-1.5 3.9-.2.2-.4.1-.3-.1.3-.7.9-2.4.6-2.9z"/>
    </svg>
  )
}
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { resolveSite } from '@/lib/site'
import type { SlideProps } from '@/components/SlideWizard'
import { copy, type PaymentMethodCopy, type Slide5PaymentCopy } from './Slide5Payment.copy'

const ease = [0.16, 1, 0.3, 1] as const

type PaymentIcon = 'gift-card' | 'transfer' | 'crypto' | 'cash-post'

type MethodKey = 'giftCard' | 'transfer' | 'crypto' | 'cashPost'

// Row order. All copy for each method lives in Slide5Payment.copy.ts.
const METHODS: readonly { key: MethodKey; icon: PaymentIcon }[] = [
  { key: 'giftCard', icon: 'gift-card' },
  { key: 'transfer', icon: 'transfer' },
  { key: 'crypto', icon: 'crypto' },
  { key: 'cashPost', icon: 'cash-post' },
]

/* ─── Method icon (used in row + sticky header) ─────────────────── */

function MethodIcon({ icon }: { icon: PaymentIcon }) {
  if (icon === 'gift-card') {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="6.5" width="17" height="11" rx="2" />
        <path d="M3.5 10h17" />
        <path d="M7 14h4" />
        <path d="M15.5 14h2" />
      </svg>
    )
  }
  if (icon === 'transfer') {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 10h16L12 5 4 10Z" />
        <path d="M6 10v7M10 10v7M14 10v7M18 10v7" />
        <path d="M4 19h16" />
      </svg>
    )
  }
  if (icon === 'crypto') {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
        <path d="M10 8h3.2a2 2 0 0 1 0 4H10V8Z" />
        <path d="M10 12h3.7a2 2 0 0 1 0 4H10v-4Z" />
        <path d="M9 8h1M9 16h1M12 6v2M14 6v2M12 16v2M14 16v2" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z" />
      <path d="m4 8 8 6 8-6" />
      <path d="M9 5h6a2 2 0 0 1 2 2v1H7V7a2 2 0 0 1 2-2Z" />
      <path d="M10 17h4" />
    </svg>
  )
}

/* ─── Per-method panel visuals ──────────────────────────────────── */

type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>

// Multi-color Google Play triangle. The simple-icons version is single-color,
// so we render the canonical four-color mark (blue / green / yellow / red) ourselves.
// Geometry: a play triangle with vertices A (top-left), B (bottom-left), C (right
// apex), split into four wedges by a horizontal centre line and a small notch
// near the apex that produces the yellow tip.
function GooglePlayTriangle({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {/* Blue: top-left wedge */}
      <path d="M3.6 2.2 L13.6 12 L3.6 21.8 Z" fill="#4285F4" />
      {/* Green: top wedge from top-left vertex to the apex along the upper edge */}
      <path d="M3.6 2.2 L18.6 10.85 L13.6 12 Z" fill="#34A853" />
      {/* Yellow: small wedge at the right apex */}
      <path d="M18.6 10.85 L20.6 12 L18.6 13.15 L13.6 12 Z" fill="#FBBC05" />
      {/* Red: bottom wedge */}
      <path d="M3.6 21.8 L13.6 12 L18.6 13.15 Z" fill="#EA4335" />
    </svg>
  )
}

// Google Play card lockup: four-color triangle paired with a "Google Play"
// wordmark, so the card actually reads as Google Play and not just a generic
// colored arrow.
function GooglePlayMark({ className = '', ...rest }: { className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} {...rest}>
      <GooglePlayTriangle className="h-full w-auto" />
      <span
        className="font-semibold tracking-tight text-slate-700"
        style={{ fontSize: '0.62em', lineHeight: 1 }}
      >
        Google Play
      </span>
    </span>
  )
}

// Steam card lockup: simple-icons Steam glyph paired with the "Steam" wordmark.
function SteamMark({ className = '', ...rest }: { className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-white ${className}`} {...rest}>
      <SiSteam className="h-full w-auto" aria-hidden="true" />
      <span
        className="font-semibold tracking-tight"
        style={{ fontSize: '0.7em', lineHeight: 1 }}
      >
        Steam
      </span>
    </span>
  )
}

const GIFT_CARD_BRANDS: Array<{
  name: string
  bg: string
  logoColor: string
  logo: IconType
  logoClass: string
  hideLabel?: boolean
  /** US prepaid products not sold in Qatar; hidden on the Qatar face. */
  usOnly?: boolean
}> = [
  // Apple Gift Card: black face, white logo
  { name: 'Apple', bg: 'bg-black', logoColor: 'text-white', logo: SiApple, logoClass: 'h-5 w-5' },
  // Google Play: white face with subtle border, four-color play triangle + wordmark
  { name: 'Google Play', bg: 'bg-white ring-1 ring-slate-200', logoColor: '', logo: GooglePlayMark, logoClass: 'h-6 text-[18px]' },
  // Amazon: dark navy face, signature orange smile
  { name: 'Amazon', bg: 'bg-[#131A22]', logoColor: 'text-[#FF9900]', logo: SiAmazon, logoClass: 'h-5 w-5' },
  // Steam: classic dark blue, white logo + wordmark
  { name: 'Steam', bg: 'bg-[#1b2838]', logoColor: '', logo: SteamMark, logoClass: 'h-6 text-[18px]' },
  // Walmart: bright blue, yellow spark
  { name: 'Walmart', bg: 'bg-[#0071dc]', logoColor: 'text-[#FFC220]', logo: SiWalmart, logoClass: 'h-14 w-14', usOnly: true },
  // Vanilla Visa: pale background, classic Visa blue wordmark
  { name: 'Vanilla Visa', bg: 'bg-[#f5f5f7]', logoColor: 'text-[#1A1F71]', logo: SiVisa, logoClass: 'h-7 w-7', usOnly: true },
]

function GiftCardVisual({ copy }: { copy: Slide5PaymentCopy }) {
  // Walmart and Vanilla Visa are US prepaid products, so the Qatar face shows
  // the four global brands in a two-up grid instead.
  const qatar = resolveSite() === 'qatar'
  const brands = qatar ? GIFT_CARD_BRANDS.filter((b) => !b.usOnly) : GIFT_CARD_BRANDS
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-4 pb-4 pt-3.5">
      <p className="text-[13px] font-medium text-slate-500">
        {copy.giftCardVisualTitle}
      </p>
      <div className={`mt-3 grid gap-2 ${qatar ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {brands.map((brand) => {
          const Logo = brand.logo
          return (
            <div
              key={brand.name}
              className={`relative overflow-hidden rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.06)] ${brand.bg} aspect-[1.6/1] flex items-center justify-center px-2`}
              aria-label={copy.giftCardAriaLabel.replace('{brand}', brand.name)}
            >
              {/* Subtle diagonal sheen, like a real plastic card */}
              <span
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/5"
                aria-hidden="true"
              />
              <Logo className={`${brand.logoClass} ${brand.logoColor} relative`} aria-hidden="true" />
              {/* Magnetic strip detail along the bottom */}
              <span
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] bg-black/15"
                aria-hidden="true"
              />
            </div>
          )
        })}
      </div>
      <p className="mt-3 text-[11px] leading-snug text-slate-500">
        {copy.giftCardVisualNote}
      </p>
    </div>
  )
}

function WireTransferVisual({ copy }: { copy: Slide5PaymentCopy }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
      <p className="text-center text-[13px] font-medium text-slate-500">
        {copy.wireVisualTitle}
      </p>

      {/* Phone shell, top portion only, bottom edge is open so the screen
          appears to continue beyond the visible area. */}
      <div className="mt-3 mx-auto w-[224px] overflow-hidden rounded-t-[32px] bg-[#0f172a] px-[3px] pt-[3px] shadow-[0_18px_38px_rgba(15,23,42,0.16)] ring-1 ring-slate-900/10">
        <div className="relative rounded-t-[29px] bg-white">
          {/* Status bar + notch */}
          <div className="relative flex items-center justify-between px-4 pt-2 pb-1 text-[8px] font-medium text-slate-500">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <svg className="h-[7px] w-[10px]" viewBox="0 0 10 7" fill="none" aria-hidden="true">
                <rect x="0.75" y="1" width="6.2" height="5" rx="1" stroke="currentColor" strokeWidth="0.9" />
                <path d="M7.6 2.4V4.6" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
                <rect x="1.7" y="1.95" width="4.1" height="3.1" rx="0.55" fill="currentColor" />
              </svg>
            </div>
            <div className="pointer-events-none absolute left-1/2 top-1.5 -translate-x-1/2">
              <div className="h-[8px] w-[60px] rounded-full bg-[#0f172a]" />
            </div>
          </div>

          {/* App header */}
          <div className="grid grid-cols-[20px_1fr_20px] items-center px-3.5 pt-3 pb-2">
            <svg viewBox="0 0 20 20" className="h-4 w-4 text-slate-700 dir-flip" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4 6 10l6 6" />
            </svg>
            <p className="text-center text-[11px] font-semibold tracking-tight text-slate-900">
              {copy.wireAppTitle}
            </p>
            <span />
          </div>

          {/* Recipient */}
          <div className="px-4 pt-2">
            <div className="flex items-center justify-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#0f4ea3] text-white">
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor" aria-hidden="true">
                  <path d="M12 2 2 7v2h20V7L12 2zm-7 9v7H3v2h18v-2h-2v-7h-2v7h-3v-7h-2v7h-2v-7H8v7H5v-7z" />
                </svg>
              </span>
              <div className="text-start leading-tight">
                <p className="text-[10px] font-semibold text-slate-900">
                  {copy.wireSendTo}
                </p>
                <p className="text-[8px] text-slate-500">{copy.wireExternalAccount}</p>
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="mt-3 px-4 text-center">
            {/* Amounts always read left-to-right; the currency sits before
                (US "$") or after (Arabic "ر.ق") per the copy. */}
            <div className="inline-flex items-baseline" dir="ltr">
              {copy.wireAmountPrefix && (
                <span className="text-[14px] font-semibold text-slate-900">{copy.wireAmountPrefix}</span>
              )}
              <span className="ms-0.5 text-[28px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
                {copy.wireAmountWhole}
              </span>
              <span className="ms-0.5 text-[10px] font-semibold text-slate-900 tabular-nums">
                {copy.wireAmountCents}
              </span>
              {copy.wireAmountSuffix && (
                <span className="ms-1 text-[14px] font-semibold text-slate-900">{copy.wireAmountSuffix}</span>
              )}
            </div>
            <div className="mt-2 border-t border-slate-200 pt-1.5">
              <p className="text-[8px] text-slate-500">
                {copy.wireRemainingLabel}{copy.wireRemainingLimit}
              </p>
            </div>
          </div>

          {/* From row */}
          <div className="mt-2 px-4">
            <div className="flex items-start justify-between gap-2 border-t border-slate-200 py-2">
              <p className="text-[10px] font-medium text-[#0f4ea3]">{copy.wireFromLabel}</p>
              <div className="flex items-center gap-1">
                <div className="text-end">
                  <p className="text-[9px] font-semibold text-slate-900">
                    {copy.wireAccountName}
                  </p>
                  <p className="text-[7px] text-slate-500">
                    {copy.wireAvailableLabel}{copy.wireAccountAvailable}
                  </p>
                </div>
                <svg viewBox="0 0 12 12" className="h-2 w-2 text-slate-400" fill="currentColor" aria-hidden="true">
                  <path d="M2 4h8L6 9z" />
                </svg>
              </div>
            </div>

            {/* Delivery row */}
            <div className="flex items-center justify-between border-t border-slate-200 py-2">
              <p className="text-[10px] font-medium text-[#0f4ea3]">{copy.wireDeliveryLabel}</p>
              <div className="flex items-center gap-1">
                <p className="text-[9px] font-semibold text-slate-900">{copy.wireDeliveryValue}</p>
                <svg viewBox="0 0 12 12" className="h-2 w-2 text-slate-400" fill="currentColor" aria-hidden="true">
                  <path d="M2 4h8L6 9z" />
                </svg>
              </div>
            </div>

            {/* Subtle "new payee" red-flag tag, sitting where the bank's own warning would appear */}
            <div className="border-t border-slate-200 py-2">
              <span className="inline-flex items-center gap-1 text-[8px] font-medium text-rose-600">
                <span className="h-1 w-1 rounded-full bg-rose-500" />
                {copy.wireNewPayee}
              </span>
            </div>
          </div>

          {/* Continue CTA, clipped flush to the open bottom edge */}
          <div className="px-4 pb-3">
            <div className="rounded-full bg-[#0f4ea3] py-2 text-center">
              <p className="text-[10px] font-semibold text-white">{copy.wireContinue}</p>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-snug text-slate-500">
        {copy.wireFooter}
      </p>
    </div>
  )
}

// Note: crypto and cash-by-post panels intentionally have no header visual yet.
// We removed the earlier ATM and doorstep mocks while we figure out a stronger
// treatment for each. The text content still carries the warning.
function PanelVisual({ icon, copy }: { icon: PaymentIcon; copy: Slide5PaymentCopy }) {
  if (icon === 'gift-card') return <GiftCardVisual copy={copy} />
  if (icon === 'transfer') return <WireTransferVisual copy={copy} />
  return null
}

/* ─── Reusable section atoms ────────────────────────────────────── */

function Section({
  heading,
  children,
}: {
  heading: string
  children: ReactNode
}) {
  return (
    <section>
      <h4 className="text-base font-semibold text-slate-900">{heading}</h4>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function BulletList({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-[15px] leading-relaxed text-slate-600">
          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

function PhraseList({
  items,
  quoteOpen,
  quoteClose,
}: {
  items: readonly string[]
  quoteOpen: string
  quoteClose: string
}) {
  return (
    <div className="space-y-3 border-s-2 border-slate-200 ps-4">
      {items.map((phrase) => (
        <p key={phrase} className="text-[15px] italic leading-relaxed text-slate-600">
          {quoteOpen}{phrase}{quoteClose}
        </p>
      ))}
    </div>
  )
}

/* ─── Main slide ────────────────────────────────────────────────── */

export default function Slide5Payment(_props: SlideProps) {
  const [selectedKey, setSelectedKey] = useState<MethodKey | null>(null)
  const c = useSlideCopy(copy)
  // The panel sits at the inline end; in RTL that is the left edge, so it
  // must slide in from the left. Rows slide in from the reading start.
  const rtl = useLocale() === 'ar'
  const offscreen = rtl ? '-100%' : '100%'
  const rowOffset = rtl ? 12 : -12

  const selected = selectedKey ? METHODS.find((m) => m.key === selectedKey) ?? null : null
  const displayedMethod: PaymentMethodCopy | null = selectedKey ? c[selectedKey] : null

  return (
    <div className="flex flex-col items-start text-start px-4 max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease }}
        className="border-s-4 border-red-400 ps-6"
      >
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight leading-[1.15]">
          {c.heading}
        </h2>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6, ease }}
        className="mt-8 text-xl sm:text-2xl text-slate-500 leading-relaxed"
      >
        {c.intro}
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8, ease }}
        className="mt-6 w-full"
      >
        {METHODS.map((method, i) => (
          <motion.button
            key={method.key}
            initial={{ opacity: 0, x: rowOffset }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.85 + i * 0.1, ease }}
            onClick={() => setSelectedKey(method.key)}
            className="group flex w-full items-center gap-4 py-2.5 text-start border-b border-red-100 last:border-0 cursor-pointer"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 text-sm font-bold">
              <MethodIcon icon={method.icon} />
            </span>
            <span className="text-lg sm:text-xl font-medium text-slate-800">
              {c[method.key].label}
            </span>
            <span className="ms-auto text-sm font-medium text-slate-400 transition-colors group-hover:text-slate-600">
              {c.learnMore}
            </span>
          </motion.button>
        ))}
      </motion.div>

      <AnimatePresence>
        {displayedMethod && selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setSelectedKey(null)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-s border-slate-200 bg-white text-start shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/90 px-6 py-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                    <MethodIcon icon={selected.icon} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {c.panelEyebrow}
                    </p>
                    <h3 className="mt-0.5 text-xl font-semibold text-slate-900">
                      {displayedMethod.title}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedKey(null)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                  aria-label={c.closeLabel}
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <PanelVisual icon={selected.icon} copy={c} />

                <p
                  className={`text-[15px] leading-relaxed text-slate-600 ${
                    selected.icon === 'gift-card' || selected.icon === 'transfer'
                      ? 'mt-6'
                      : ''
                  }`}
                >
                  {displayedMethod.intro}
                </p>

                <div className="mt-7 space-y-7">
                  <Section heading={c.whyHeading}>
                    <BulletList items={displayedMethod.whyScammers} />
                  </Section>

                  <Section heading={c.whatHeading}>
                    <BulletList items={displayedMethod.inPractice} />
                  </Section>

                  {displayedMethod.channels && (
                    <Section heading={displayedMethod.channels.heading}>
                      <BulletList items={displayedMethod.channels.items} />
                    </Section>
                  )}

                  <Section heading={c.phrasesHeading}>
                    <PhraseList
                      items={displayedMethod.phrases}
                      quoteOpen={c.phraseQuoteOpen}
                      quoteClose={c.phraseQuoteClose}
                    />
                  </Section>
                </div>

                {/* Editorial pull-quote treatment for the absolute rule. */}
                <div className="mt-8 border-t border-slate-200 pt-6">
                  <p className="text-[13px] font-medium text-slate-500">
                    {c.ruleLabel}
                  </p>
                  <p className="mt-2 text-[18px] font-medium leading-snug tracking-tight text-slate-900">
                    {displayedMethod.rule}
                  </p>
                </div>

                {/* Quieter follow-up. Same typographic family, lower contrast. */}
                <div className="mt-7">
                  <p className="text-[13px] font-medium text-slate-500">
                    {c.goneLabel}
                  </p>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
                    {displayedMethod.ifAlreadyGone}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
